import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/Button";
import {
  FileText,
  Quote,
  Receipt,
  Banknote,
  ShieldCheck,
  Zap,
  Lock,
  Building2,
} from "lucide-react";

export default function Landing() {
  return (
    <div className="page-gradient text-slate-800">
      <header className="fixed inset-x-0 top-0 z-40 border-b border-white/40 bg-white/60 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-brand-500/25">
              AB
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold tracking-tight leading-none">AIBoek</span>
              <span className="text-[0.625rem] text-slate-500 uppercase tracking-wider">a product of AICORE BV</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-xl hover:bg-white/60 transition-colors"
            >
              Inloggen
            </Link>
            <Link to="/packages">
              <Button>Start gratis proef</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden pt-36 pb-24">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-brand-300/40 blur-3xl animate-liquid-sway" />
          <div className="absolute top-40 -left-20 w-[400px] h-[400px] rounded-full bg-indigo-300/30 blur-3xl animate-liquid-sway" style={{ animationDelay: "-2s" }} />
        </div>
        <div className="relative mx-auto max-w-7xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="chip-gloss mb-7">
              <Zap size={24} className="text-brand-500" />
              AI-ondersteunde boekhouding voor ondernemers
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight mb-6 text-gradient">
              Slimme boekhouding voor je bedrijf
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-9 leading-relaxed max-w-2xl">
              Maak professionele facturen en offertes, registreer betalingen en uitgaven, en houd je
              administratie op orde — veilig in de cloud. Speciaal voor zzp'ers en kleine bedrijven.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/packages">
                <Button size="lg">Bekijk pakketten</Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline">Direct aanmelden</Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 relative">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="text-3xl font-bold text-center mb-4">Alles wat je nodig hebt</h2>
          <p className="text-center text-slate-500 max-w-xl mx-auto mb-14">
            Van offerte tot betaling, AIBoek regelt je administratie in één overzichtelijk platform.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: FileText, title: "Facturen", desc: "Snelle facturen met BTW en professionele layout." },
              { icon: Quote, title: "Offertes", desc: "Converteer offertes eenvoudig naar factuur." },
              { icon: Receipt, title: "Uitgaven", desc: "Upload bonnetjes; OCR leest bedrag en datum." },
              { icon: Banknote, title: "Betalingen", desc: "Koppel betalingen aan openstaande facturen." },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="surface-card p-6 hover:-translate-y-1 transition-transform"
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white mb-4 shadow-lg shadow-brand-500/20">
                  <f.icon size={33} />
                </div>
                <h3 className="font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-slate-500 text-sm">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 relative">
        <div className="mx-auto max-w-7xl px-6">
          <div className="surface-card p-10 md:p-14">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
              <div className="space-y-8 flex-1">
                <div>
                  <h2 className="text-3xl font-bold mb-3 flex items-center gap-3 text-slate-900">
                    <ShieldCheck size={42} className="text-brand-500" />
                    Veilig en gescheiden per bedrijf
                  </h2>
                  <p className="text-slate-500 text-lg max-w-xl">
                    AIBoek — een product van AICORE BV — draait veilig in de cloud. Elke klant krijgt een
                    eigen afgeschermde omgeving: eigen facturen, offertes, contacten en uitgaven. Geen
                    vermenging van gegevens tussen bedrijven.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="flex items-start gap-4">
                    <div className="orb bg-gradient-to-br from-emerald-400 to-emerald-600">
                      <Lock size={30} />
                    </div>
                    <div>
                      <p className="font-bold">Beveiligd met hashing</p>
                      <p className="text-sm text-slate-500">Wachtwoorden en gevoelige gegevens worden veilig gehasht opgeslagen.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="orb bg-gradient-to-br from-indigo-400 to-indigo-600">
                      <Building2 size={30} />
                    </div>
                    <div>
                      <p className="font-bold">Eigen omgeving</p>
                      <p className="text-sm text-slate-500">Elk bedrijf logt in met eigen e-mail en ziet alleen eigen administratie.</p>
                    </div>
                  </div>
                </div>
              </div>
              <Link to="/register">
                <Button size="lg" variant="outline" className="border-slate-300 hover:border-brand-500">
                  Account aanmaken
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
